/**
 * Mobile Phase 2, chunk 1 (docs/mobile-design.md §4.7, §4.14): the phone's full feed, announcements with their
 * Acknowledge bar, and the kudos sheet. Pure rules: filter wording, the inline status line on combined cards, what
 * the Acknowledge bar shows, and when a kudos draft can be posted.
 */
import { FEED_SCOPE_LABEL, FEED_TYPES, FEED_TYPE_LABEL, feedNewestComment, type FeedItem, type FeedScope, type FeedType } from '../home/feed';
import type { Announcement } from '../kb/types';
import { activityText, issueActivity, type ActivitySeg } from '../home/issueActivity';
import { formatInstantGB } from '../gbDate';

// ------------------------------------------------------------------------------------------------ filters
export interface FeedFilters { types: FeedType[]; scope: FeedScope }
export const DEFAULT_FEED_FILTERS: FeedFilters = { types: [], scope: 'all' };

/** Read stored filters, dropping anything unknown (an old app version may have saved other types). */
export function parseFeedFilters(raw: unknown): FeedFilters {
  const v = (raw && typeof raw === 'object' ? raw : {}) as Partial<Record<keyof FeedFilters, unknown>>;
  const types = Array.isArray(v.types) ? v.types.filter((t): t is FeedType => (FEED_TYPES as readonly unknown[]).includes(t)) : [];
  const scope = typeof v.scope === 'string' && v.scope in FEED_SCOPE_LABEL ? (v.scope as FeedScope) : 'all';
  return { types: [...new Set(types)], scope };
}
/** Ticking every type is the same as All. */
export function toggleFeedType(f: FeedFilters, t: FeedType): FeedFilters {
  const types = f.types.includes(t) ? f.types.filter(x => x !== t) : [...f.types, t];
  return { ...f, types: types.length === FEED_TYPES.length ? [] : FEED_TYPES.filter(x => types.includes(x)) };
}
/** The number on the Filter button: one per ticked type, one for a scope other than Everywhere. */
export const feedFilterCount = (f: FeedFilters) => f.types.length + (f.scope === 'all' ? 0 : 1);
/** The line under the header: "All news", "Kudos and Issues", "3 kinds · My teams". */
export function feedFilterSummary(f: FeedFilters): string {
  const kinds = !f.types.length ? 'All news'
    : f.types.length === 1 ? FEED_TYPE_LABEL[f.types[0]!]
    : f.types.length === 2 ? `${FEED_TYPE_LABEL[f.types[0]!]} and ${FEED_TYPE_LABEL[f.types[1]!]}`
    : `${f.types.length} kinds`;
  return f.scope === 'all' ? kinds : `${kinds} · ${FEED_SCOPE_LABEL[f.scope]}`;
}

// ------------------------------------------------------------------------------------------------ compact cards
const names = (a: { name: string }[]) => (a.length <= 2 ? a.map(x => x.name).join(' and ') : `${a[0]!.name}, ${a[1]!.name} and ${a.length - 2} more`);
const SEVERITY: Record<string, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
/** t212u: "Severity: Medium" for the issue card's severity icon. */
export const severityLabel = (s: string) => `Severity: ${SEVERITY[s] ?? s}`;
/** t212u: "View 3 earlier comments" under the latest comment, or null when the latest is the only one. */
export const earlierCommentsLabel = (total: number) => (total > 1 ? `View ${total - 1} earlier comment${total - 1 === 1 ? '' : 's'}` : null);

/**
 * The status line a combined card keeps inline on the phone (issue, task, discussion, kudos with comments): who did
 * the latest thing and what is new, so the card stays one tap from the item without opening its thread.
 * t246u: an issue's line says what changed ("Willow Parker set severity to High · Northwich"); people are pieces of
 * their own so they open their profile, and the building is apart so it opens its page.
 */
export interface FeedStatus { segs: ActivitySeg[]; building: { id: string | null; name: string } | null }
export function feedStatus(i: FeedItem): FeedStatus | null {
  const th = 'thread' in i ? i.thread : undefined;
  const talked = Boolean(th && th.count > 0 && 'event_at' in i && i.event_at && i.event_at < i.at);
  // t212u: no "N new comments" (the comments button carries the count and an unread dot) and no severity text on the
  // issue line (the severity icon under the type icon shows it).
  switch (i.kind) {
    case 'issue': {
      // The card's comment row already shows the newest comment, so the line keeps to the issue's own latest change
      // unless there is no comment row.
      const segs = issueActivity(i, { withComment: !feedLastComment(i) });
      return { segs, building: i.issue.location_name ? { id: i.issue.location_id ?? null, name: i.issue.location_name } : null };
    }
    case 'task_completed': {
      const segs: ActivitySeg[] = talked && th ? [`${names(th.actors)} commented`] : i.task.actor_name ? ['Done by ', i.task.actor_id ? { id: i.task.actor_id, name: i.task.actor_name } : i.task.actor_name] : ['Done'];
      return { segs: i.task.project_name ? [...segs, ` · ${i.task.project_name}`] : segs, building: null };
    }
    case 'discussion': return { segs: [`${names(i.thread.actors)} commented`], building: null };
    case 'announcement': case 'kudos': case 'birthday': case 'event': return null;
    default: return null;
  }
}
export function feedStatusLine(i: FeedItem): string | null {
  const s = feedStatus(i);
  return s ? [activityText(s.segs), s.building?.name].filter(Boolean).join(' · ') : null;
}
/** The newest comment on a combined card, one line: "Sarah: On my way". */
/** t188u: the newest comment for a card's preview line: who (first name, shown apart from the text) and what. */
export interface FeedCommentPreview {
  actor_id: string | null; name: string; body: string;
  /** t358u: a reply: whose comment it answers (first name), shown as "replied to Euan" before the text. */
  reply_to?: string | null;
  /** t358u: your own comment says "You"; its avatar still shows your initials. */
  avatar_name?: string;
}
/** t358u: the newest comment of any kind (replies and the viewer's own, as "You"), matching the Latest line. */
export function feedLastComment(i: FeedItem): FeedCommentPreview | null {
  const c = feedNewestComment(i);
  if (!c) return null;
  const body = c.body.replace(/\s+/g, ' ').trim();
  const first = (n: string) => n.split(' ')[0] || 'Someone';
  return body ? { actor_id: c.actor_id, name: c.mine ? 'You' : first(c.actor_name), ...(c.mine ? { avatar_name: c.actor_name } : {}), body, ...(c.reply_to ? { reply_to: first(c.reply_to) } : {}) } : null;
}

// ------------------------------------------------------------------------------------------------ acknowledge bar
export type AckBar =
  | { state: 'pending'; label: string; note: string | null; overdue: boolean }
  | { state: 'done'; note: string }
  | { state: 'author'; note: string };
const day = (iso: string, tz: string) => formatInstantGB(iso, 'ccc d LLL HH:mm', tz);

/** What the bar pinned to the bottom of an announcement shows; null when it doesn't ask for acknowledgement. */
export function ackBar(a: Pick<Announcement, 'require_ack' | 'ack_required' | 'acked_version' | 'version' | 'ack_due_at'>, now = Date.now(), tz = 'Europe/London'): AckBar | null {
  if (!a.require_ack) return null;
  if (a.ack_required) {
    const overdue = Boolean(a.ack_due_at && Date.parse(a.ack_due_at) < now);
    return { state: 'pending', label: 'Acknowledge', overdue, note: a.ack_due_at ? `${overdue ? 'Was due' : 'Due'} ${day(a.ack_due_at, tz)}` : null };
  }
  if (a.acked_version === a.version) return { state: 'done', note: 'You acknowledged this' };
  if (a.acked_version != null) return { state: 'done', note: `You acknowledged version ${a.acked_version}` };
  return { state: 'author', note: 'You asked people to acknowledge this' };
}
/** The list's bar: the next announcement waiting for you (oldest due first, then oldest posted). */
export function nextToAcknowledge<T extends Pick<Announcement, 'id' | 'ack_required' | 'ack_due_at' | 'publish_at'>>(list: T[]): { next: T | null; count: number } {
  const waiting = list.filter(a => a.ack_required);
  const key = (a: T) => [a.ack_due_at ?? '9999', a.publish_at].join('|');
  const sorted = [...waiting].sort((x, y) => (key(x) < key(y) ? -1 : key(x) > key(y) ? 1 : 0));
  return { next: sorted[0] ?? null, count: waiting.length };
}

// ------------------------------------------------------------------------------------------------ kudos sheet
export interface KudosDraft { body: string; people: string[]; teams: string[]; uploading?: boolean }
export const KUDOS_MAX = 5000;
/** Why Post is disabled, or null when it can go. */
export function kudosBlocker(d: KudosDraft): string | null {
  if (!d.people.length && !d.teams.length) return 'Pick someone to thank';
  if (!d.body.trim()) return 'Say what they did';
  if (d.body.length > KUDOS_MAX) return `Keep it under ${KUDOS_MAX} characters`;
  if (d.uploading) return 'Wait for the photo to upload';
  return null;
}

// ------------------------------------------------------------------------------------------------ unread markers (Home, t182u)
/** The newest item time seen on Home, per device; cards newer than the last visit get the unread marker. */
export const FEED_SEEN_KEY = 'collab.home.feed.seen';
/** Unread: an announcement I haven't opened, or anything newer than my last visit (nothing is "new" on a first visit). */
export function feedItemUnread(i: FeedItem, seenAt: string | null | undefined): boolean {
  if (i.kind === 'announcement' && !i.announcement.read_at) return true;
  return Boolean(seenAt) && i.at > seenAt!;
}
/** What to remember as seen after this visit: the newest item time, never going backwards. */
export function nextFeedSeen(items: readonly Pick<FeedItem, 'at'>[], seenAt: string | null | undefined): string | null {
  const top = items.reduce<string | null>((m, i) => (!m || i.at > m ? i.at : m), null);
  if (!top) return seenAt ?? null;
  return !seenAt || top > seenAt ? top : seenAt;
}
